import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errorMessage } from "@/lib/api";

const NONE = "__none__";

function toForm(fields, record) {
  return Object.fromEntries(fields.map((f) => {
    const v = record?.[f.name];
    if (f.type === "list") return [f.name, (v || []).join("\n")];
    if (f.type === "multiref") return [f.name, v || []];
    if (f.type === "boolean" || f.type === "check") return [f.name, String(v ?? f.default ?? false)];
    const fallback = f.default ?? (f.type === "select" && !f.nullable ? f.options[0][0] : "");
    return [f.name, v ?? fallback];
  }));
}

function toPayload(fields, values) {
  return Object.fromEntries(fields.map((f) => {
    const v = values[f.name];
    if (f.type === "list") return [f.name, String(v || "").split("\n").map((s) => s.trim()).filter(Boolean)];
    if (f.type === "number") return [f.name, v === "" || v == null ? null : Number(v)];
    if (f.type === "boolean" || f.type === "check") return [f.name, v === "true"];
    if (v === "" || v === NONE) return [f.name, null];
    return [f.name, v];
  }));
}

function FieldInput({ field, value, onChange, lookups, testid }) {
  if (field.type === "check")
    return (
      <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm hover:bg-accent">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" checked={value === "true"} onChange={(e) => onChange(String(e.target.checked))} data-testid={testid} />
        <span>{field.hint}</span>
      </label>
    );
  if (field.type === "multiref") {
    const options = Object.entries(lookups?.[field.ref] || {});
    const selected = new Set(Array.isArray(value) ? value : []);
    const toggle = (id) => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); onChange([...next]); };
    return (
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2" data-testid={testid}>
        {options.length === 0 && <p className="text-xs text-muted-foreground">No records available yet.</p>}
        {options.map(([id, label]) => (
          <label key={id} className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-xs hover:bg-accent">
            <input type="checkbox" className="mt-0.5 accent-[hsl(var(--primary))]" checked={selected.has(id)} onChange={() => toggle(id)} data-testid={`${testid}-option-${id}`} />
            <span>{label}</span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "textarea" || field.type === "list")
    return <Textarea id={field.name} rows={field.type === "list" ? 3 : 4} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  if (field.type === "select" || field.type === "ref" || field.type === "boolean") {
    const options = field.type === "ref" ? Object.entries(lookups?.[field.ref] || {}) : field.type === "boolean" ? [["true", "Yes"], ["false", "No"]] : field.options;
    const allowNone = field.type === "ref" || field.nullable;
    return (
      <Select value={value ? String(value) : allowNone ? NONE : undefined} onValueChange={onChange}>
        <SelectTrigger id={field.name} data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value={NONE}>None</SelectItem>}
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  return <Input id={field.name} type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
    step={field.type === "number" ? field.step || "any" : undefined} min={field.min}
    placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;;
}

export function EntityFormDialog({ open, onOpenChange, title, fields, record, defaults, lookups, onSubmit, testid = "entity-form" }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setValues(toForm(fields, { ...defaults, ...record })); setError(null); }
  }, [open, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.filter((f) => f.required && !String(values[f.name] ?? "").trim() || (f.required && values[f.name] === NONE));
    if (missing.length) { setError(`Please complete: ${missing.map((f) => f.label).join(", ")}`); return; }
    setSaving(true); setError(null);
    try { await onSubmit(toPayload(fields, values)); onOpenChange(false); }
    catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.filter((f) => !f.showIf || f.showIf(values)).map((f) => (
            <div key={f.name} className={`space-y-1.5 ${f.span === 2 ? "sm:col-span-2" : ""}`}>
              <Label htmlFor={f.name} className="text-xs">{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
              <FieldInput field={f} value={values[f.name] ?? ""} lookups={lookups} testid={`${testid}-field-${f.name}`}
                onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} />
            </div>
          ))}
          {error && <p role="alert" data-testid={`${testid}-error`} className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive sm:col-span-2">{error}</p>}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={`${testid}-submit`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
